import * as accounting from "accounting";
import * as _ from "lodash";

const validatePrice = (price: IPriceInCurrency) => {
  if (typeof price.price !== "number") {
    throw new Error(`Type error: property 'price' must be a number. ${price.price} is a ${typeof price.price}`);
  }

  if (!price.currency) {
    throw new Error(`The 'currency' property of a price must have a value`);
  }
};

export function add(...prices: IPriceInCurrency[]): IPriceInCurrency {
  let total: IPriceInCurrency = null;
  for (const price of prices) {
    if (price == null) {
      continue;
    }

    validatePrice(price);

    if (total) {
      if (price.currency.id !== total.currency.id) {
        throw new Error("can only add prices of the same currency");
      }

      total = round(_.assign({}, total, { price: total.price + price.price }));
    }
    else {
      total = price;
    }
  }
  return total;
}

export function subtract(x: IPriceInCurrency, y: IPriceInCurrency): IPriceInCurrency {
  if (x.currency.id !== y.currency.id) {
    throw new Error("can only subtract prices of the same currency");
  }

  validatePrice(x);
  validatePrice(y);

  return round(_.assign({}, x, { price: x.price - y.price }));
}

export function multiply(price: IPriceInCurrency, multiplier: number): IPriceInCurrency {
  validatePrice(price);
  return round(_.assign({}, price, { price: price.price * multiplier }));
}

export function divide(price: IPriceInCurrency, divisor: number): IPriceInCurrency {
  if ((divisor === 0) || isNaN(divisor)) {
    throw new Error(`cannot divide by ${divisor}`);
  }

  validatePrice(price);
  return round(_.assign({}, price, { price: price.price / divisor }));
}

/**
 * Rounds the price to the decimal places for its currency
 */
export function round(price: IPriceInCurrency): IPriceInCurrency {
  return _.extend({}, price, { price: roundNumber(price) });
}

export function greaterThan(x: IPriceInCurrency, y: IPriceInCurrency): boolean {
  if (x.currency.id !== y.currency.id) {
    throw new Error("can only compare prices of the same currency");
  }

  return roundNumber(x) > roundNumber(y);
}

export function isZero(x: IPriceInCurrency): boolean {
  return toFixed(x) === toFixed({ price: 0, currency: x.currency });
}

export function toFixed(price: IPriceInCurrency): string {
  if (!price.currency) {
    throw new Error("Can't format a price without a currency");
  }
  return accounting.toFixed(price.price, price.currency.decimalPlaces);
}

export function formatForCustomer(price: IPriceInCurrency): string {
  return accounting.formatMoney(price.price, {
    symbol: price.currency.symbol,
    precision: price.currency.decimalPlaces,
  });
}

export function formatForLtoOrAdmin(price: IPriceInCurrency): string {
  return accounting.formatMoney(price.price, {
    symbol: price.currency.code,
    format: "%v %s",
    precision: price.currency.decimalPlaces,
  });
}

/**
 * Explanantion of commission rate.
 *
 * The commission rate is not a percentage of the LTOs net price.
 * It is a percentage of the customers gross price.
 * E.g If the LTO price was £4500, and the commission rate was 10%,
 * then the final customer price would be £5000 because the 10% is
 * from the customer price - not the lto price as you might think.
 */
export function addCommission(price: IPriceInCurrency, commissionRate: number): IPriceInCurrency {
  const commission: number = commissionRate / 100;
  return multiply(price, 1 / (1 - commission));
}

export function removeCommission(price: IPriceInCurrency, commissionRate: number): IPriceInCurrency {
  const commission: number = commissionRate / 100;
  return divide(price, 1 / (1 - commission));
}

export function convertForCustomer(price: IPriceInCurrency, customerCurrency: ICurrency, commissionRate: number): IPriceInCurrency {
  if (price == null || customerCurrency == null || commissionRate == null) {
    throw new Error("One or more parameters was undefined");
  }

  if (!customerCurrency.customerExchangeRate || !price.currency.ltoExchangeRate) {
    throw new Error("Exchange rates not defined");
  }

  const priceWithCommission: IPriceInCurrency = addCommission(price, commissionRate);
  const currencyConversionRate: number = customerCurrency.customerExchangeRate / price.currency.ltoExchangeRate;

  return round({
    price: priceWithCommission.price * currencyConversionRate,
    currency: customerCurrency,
  });
}

export function convertForLto(price: IPriceInCurrency, ltoCurrency: ICurrency, commissionRate: number): IPriceInCurrency {
  if (price == null || ltoCurrency == null || commissionRate == null) {
    throw new Error("One or more parameters was undefined");
  }

  if (!ltoCurrency.ltoExchangeRate || !price.currency.customerExchangeRate) {
    throw new Error("Exchange rates not defined");
  }

  const priceWithoutCommission: IPriceInCurrency = removeCommission(price, commissionRate);
  const currencyConversionRate: number = ltoCurrency.ltoExchangeRate / price.currency.customerExchangeRate;

  return round({
    price: priceWithoutCommission.price * currencyConversionRate,
    currency: ltoCurrency,
  });
}

function roundNumber(price: IPriceInCurrency): number {
  return +toFixed(price);
}
